/* eslint-disable */
import { useRef } from 'react';
import { useHookstate } from '@hookstate/core';
import { associateDispatchDeviceFromMap } from '~/features/Dispatch/services';
import { getCurrentDispatchDeviceService } from '~/features/Device/services';
import useDispatchVehicleState from '~/features/Dispatch/stores/DispatchVehicleState';
import useDispatchState from '~/features/Dispatch/stores/DispatchState';
import notify from '~/utils/notify';
import { useLang } from '~/hooks/useLang';

export interface DragPayload {
  readonly dispatchMarkerId: string;
  readonly deviceMarkerId: string;
  readonly codeDispatch: string;
  readonly deviceName: string;
}

interface UseMapDragDispatchParams {
  readonly markersFreeze: any;
  readonly loadMarkersState: (...args: any[]) => any;
  readonly map: () => any;
}

export default function useMapDragDispatch({ markersFreeze, loadMarkersState, map }: UseMapDragDispatchParams) {
  const { translate } = useLang();
  const { syncVehicleListInDispatch } = useDispatchVehicleState();
  const { getDispatchByCode } = useDispatchState();

  const confirmDragOpen = useHookstate(false);
  const confirmDragLoading = useHookstate(false);
  const dragPayload = useHookstate<DragPayload | null>(null);
  const isDraggingRef = useRef(false);

  function handleDragStateChange(dragging: boolean) {
    isDraggingRef.current = dragging;
  }

  async function validateSameDispatchGroup(data: { codeDispatch: string; deviceMarkerId: string }): Promise<boolean> {
    const dispatches = await getDispatchByCode(data.codeDispatch).promise;
    const dispatchData = dispatches?.[0] as any;
    const dispatchGroupId = dispatchData?.dispatch_group_id ?? null;

    const deviceKey = data.deviceMarkerId.replace('device-', '');
    const deviceMarkerData = markersFreeze.device[deviceKey]?.get();
    const deviceGroupId = deviceMarkerData?.groupId ?? null;

    if (dispatchGroupId != null && deviceGroupId != null && Number(dispatchGroupId) !== Number(deviceGroupId)) {
      notify({
        message: translate('Dispatch and device do not belong to the same dispatch group'),
        type: 'error',
      });
      return false;
    }

    return true;
  }

  async function validateDeviceNotAssociated(deviceMarkerId: string): Promise<boolean> {
    const deviceNumericId = Number(deviceMarkerId.replace('device-', ''));

    if (isNaN(deviceNumericId)) {
      notify({ message: translate('An error occurred'), type: 'error' });
      return false;
    }

    const currentDispatch = await getCurrentDispatchDeviceService({ deviceId: deviceNumericId });
    if (currentDispatch && currentDispatch.id) {
      notify({
        message: translate('Device already associated with another dispatch'),
        type: 'warning',
      });
      return false;
    }

    return true;
  }

  async function handleDragEnd(data: DragPayload) {
    if (!data.codeDispatch) {
      confirmDragLoading.set(false);
      dragPayload.set(data);
      confirmDragOpen.set(true);
      return;
    }

    try {
      const isSameGroup = await validateSameDispatchGroup(data);
      if (!isSameGroup) return;

      const isDeviceFree = await validateDeviceNotAssociated(data.deviceMarkerId);
      if (!isDeviceFree) return;

      confirmDragLoading.set(false);
      dragPayload.set(data);
      confirmDragOpen.set(true);
    } catch (error) {
      notify({
        message: translate('An error occurred'),
        type: 'error',
      });
    }
  }

  function handleDragConfirmClose() {
    confirmDragOpen.set(false);
    confirmDragLoading.set(false);
    dragPayload.set(null);
  }

  async function handleDragConfirmAgree() {
    const payload = dragPayload.get();
    if (!payload) return;

    const dispatchNumericId = Number(payload.dispatchMarkerId.replace('dispatch-', ''));
    const deviceNumericId = Number(payload.deviceMarkerId.replace('device-', ''));

    if (isNaN(dispatchNumericId) || isNaN(deviceNumericId)) {
      notify({ message: translate('An error occurred'), type: 'error' });
      handleDragConfirmClose();
      return;
    }

    confirmDragLoading.set(true);

    try {
      await associateDispatchDeviceFromMap(dispatchNumericId, deviceNumericId);

      notify({
        message: translate('Device associated to dispatch successfully'),
        type: 'success',
      });

      await syncVehicleListInDispatch(dispatchNumericId);

      const abort = new AbortController();
      loadMarkersState('device', abort, map().getBounds().getCenter().lat, map().getBounds().getCenter().lng);
      loadMarkersState('dispatch', abort, map().getBounds().getCenter().lat, map().getBounds().getCenter().lng);

      handleDragConfirmClose();
    } catch (error) {
      handleDragConfirmClose();
    }
  }

  return {
    confirmDragOpen,
    confirmDragLoading,
    dragPayload,
    isDraggingRef,
    handleDragStateChange,
    handleDragEnd,
    handleDragConfirmClose,
    handleDragConfirmAgree,
  };
}
